import type { ReactNode } from 'react'
import type { PaymentMethod } from '@/hooks/use-billing'
import { i18n } from '@/lib/i18n'

/**
 * Logo nhỏ cho phương thức thanh toán.
 *  - MoMo, ZaloPay, VietQR Pay, PayPal: tệp logo thật ở `public/assets/payment/` (nguồn: Wikimedia / SePay / Pixabay do chủ sản phẩm chỉ định;
 *    PNG đã cắt viền thừa + thu nhỏ). Logo thuộc chủ sở hữu thương hiệu — chỉ dùng để nhận diện phương thức thanh toán.
 *  - Visa, Mastercard, biểu tượng ngân hàng: SVG vẽ đơn giản (chưa có tệp chính thức) — thay bằng <Img> khi có.
 * Kích thước: `sm` (hàng ngang, 40×24) · `lg` (thẻ lớn, 52×32); logo dạng ngang dài (ZaloPay, PayPal, VietQR) rộng hơn để đọc được chữ.
 */
type Size = 'sm' | 'lg'
const BOX: Record<Size, { w: number; h: number; wide: number }> = { sm: { w: 40, h: 24, wide: 54 }, lg: { w: 52, h: 32, wide: 72 } }

function Mark({ label, size, children }: { label: string; size: Size; children: ReactNode }) {
  const { w, h } = BOX[size]
  return (
    <svg viewBox="0 0 40 24" width={w} height={h} role="img" aria-label={label} className="shrink-0 rounded-[5px] border bg-white">
      <title>{label}</title>
      {children}
    </svg>
  )
}

/** Logo từ tệp ảnh; `wide` = logo ngang dài (chip rộng hơn). */
function Img({ src, label, size, wide }: { src: string; label: string; size: Size; wide?: boolean }) {
  const { w, h, wide: ww } = BOX[size]
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      style={{ width: wide ? ww : w, height: h }}
      className="inline-flex shrink-0 items-center justify-center rounded-[5px] border bg-white p-[3px]"
    >
      <img src={src} alt="" loading="lazy" draggable={false} className="max-h-full max-w-full object-contain" />
    </span>
  )
}

type L = { size: Size }

const Visa = ({ size }: L) => (
  <Mark label="Visa" size={size}>
    <text x="20" y="16" textAnchor="middle" fontSize="11" fontWeight="800" fontStyle="italic" fontFamily="Arial, Helvetica, sans-serif" fill="#1A1F71">VISA</text>
  </Mark>
)

const Mastercard = ({ size }: L) => (
  <Mark label="Mastercard" size={size}>
    <circle cx="16" cy="12" r="7" fill="#EB001B" />
    <circle cx="24" cy="12" r="7" fill="#F79E1B" />
    <path d="M20 6.5a7 7 0 0 1 0 11 7 7 0 0 1 0-11z" fill="#FF5F00" />
  </Mark>
)

const Bank = ({ size }: L) => (
  <Mark label={i18n.t('billing:paymentLogos.bank')} size={size}>
    <g fill="none" stroke="#475569" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round">
      <path d="M12 9.5 20 5l8 4.5z" /><path d="M13.5 11.5v6M18 11.5v6M22 11.5v6M26.5 11.5v6M12 19.5h16" />
    </g>
  </Mark>
)

const MoMo = ({ size }: L) => <Img src="/assets/payment/momo.png" label="MoMo" size={size} />
const ZaloPay = ({ size }: L) => <Img src="/assets/payment/zalopay.svg" label="ZaloPay" size={size} wide />
const VietQr = ({ size }: L) => <Img src="/assets/payment/vietqr.png" label="VietQR Pay" size={size} wide />
const PayPal = ({ size }: L) => <Img src="/assets/payment/paypal.png" label="PayPal" size={size} wide />
// Logo SePay (sepay.vn/assets/img/brand-assets — bản màu trên nền trắng, đã cắt sát logo).
const SePay = ({ size }: L) => <Img src="/assets/payment/sepay.jpg" label="SePay" size={size} wide />

// Chưa có logo chính thức của Tingee trong repo → dấu chữ (thay bằng ảnh khi có file thương hiệu).
const Tingee = ({ size }: L) => (
  <Mark label="Tingee" size={size}>
    <text x="20" y="16" textAnchor="middle" fontSize="10" fontWeight="800" fontFamily="Arial, Helvetica, sans-serif" fill="#0F766E">Tingee</text>
  </Mark>
)

const LOGOS: Record<PaymentMethod, ((p: L) => ReactNode)[]> = {
  card: [Visa, Mastercard, MoMo, ZaloPay],
  bank: [VietQr, Bank],
  paypal: [PayPal],
  sepay: [SePay],
  tingee: [Tingee, VietQr],
}

/** Hàng logo của một phương thức (ẩn với trình đọc màn hình — tên phương thức đã có trong nhãn). */
export function MethodLogos({ id, size = 'sm', className }: { id: PaymentMethod; size?: Size; className?: string }) {
  return (
    <span className={className ?? 'flex items-center gap-1'} aria-hidden="true">
      {(LOGOS[id] ?? []).map((Logo, i) => <Logo key={i} size={size} />)}
    </span>
  )
}
